import { z } from "zod";
import { PatientAdministrativeSexSchema } from "./patient-profile.js";
import { RegisterPatientInputSchema } from "./patient-registration.js";
/**
 * The questionnaire a patient fills in before a first visit (PA-2 /
 * EXECDEV-212), Ontario-shaped: identity, health coverage and the visit.
 *
 * Its identity and coverage keys are RegisterPatientInput keys with the same
 * meaning and stricter values, so registering the patient from it (PA-6) is a
 * key pick, not a translation (`toRegisterPatientInput`). The visit keys are
 * the PA-3 ones.
 *
 * Consent is not part of it (PA-11). The patient agrees to the clinic's
 * versioned terms and privacy notice once, at booking or beside the
 * questionnaire (patient-consent.ts); treatment consent stays with the
 * clinician in person, and the uninsured-fee note is in the terms. The PA-3
 * acknowledgements and typed signature (privacyConsent, treatmentConsent,
 * financialConsent, signature) are refused like any unknown key.
 *
 * Two shapes. `PatientQuestionnaireDraftSchema` is the flat form state the
 * patient app edits and the demo lane prefills: every field may be blank.
 * `PatientQuestionnaireSchema` is the compacted, validated questionnaire a
 * submit sends, the database stores and staff read back.
 * `patientQuestionnaireFromDraft` turns one into the other and reports a
 * patient-facing message per draft field, so no app re-implements a rule.
 *
 * private.scheduling_questionnaire_valid mirrors PatientQuestionnaireSchema
 * rule for rule (key sets, lengths, the health number check digit, the plan
 * names, the postal code pattern, no year 0000); only the refusal of text
 * jsonb cannot hold (U+0000 and lone surrogates) has no copy there, since such
 * a value never reaches it. Change both together: a row the database accepts
 * but this schema rejects breaks the staff intake read.
 */
// ---------------------------------------------------------------------------
// Provinces and plan names
// ---------------------------------------------------------------------------
export const CANADIAN_PROVINCES = [
    "AB",
    "BC",
    "MB",
    "NB",
    "NL",
    "NS",
    "NT",
    "NU",
    "ON",
    "PE",
    "QC",
    "SK",
    "YT",
];
export const CanadianProvinceSchema = z.enum(CANADIAN_PROVINCES, {
    error: "Choose a province or territory",
});
/** Every province and territory but Ontario: where an out-of-province plan comes from. */
export const OtherCanadianProvinceSchema = CanadianProvinceSchema.exclude(["ON"], {
    error: "Choose the province or territory that issued your health card",
});
export const CANADIAN_PROVINCE_NAMES = {
    AB: "Alberta",
    BC: "British Columbia",
    MB: "Manitoba",
    NB: "New Brunswick",
    NL: "Newfoundland and Labrador",
    NS: "Nova Scotia",
    NT: "Northwest Territories",
    NU: "Nunavut",
    ON: "Ontario",
    PE: "Prince Edward Island",
    QC: "Quebec",
    SK: "Saskatchewan",
    YT: "Yukon",
};
/**
 * The payer name a provincial plan is recorded under, as the chart import
 * writes it (cds-import mappers/demographics.ts).
 */
export function provincialPlanName(province) {
    return `${CANADIAN_PROVINCE_NAMES[province]} Health Insurance Plan`;
}
/**
 * The exact payer name OHIP billing recognises (migration 20261007022422) and
 * private.apply_patient_health_card files the version code and expiry under.
 */
export const OHIP_PAYER_NAME = provincialPlanName("ON");
export const IFH_PAYER_NAME = "Interim Federal Health Program";
export const OTHER_PROVINCIAL_PLAN_NAMES = OtherCanadianProvinceSchema.options.map(provincialPlanName);
// ---------------------------------------------------------------------------
// Ontario health card and Canadian postal code
// ---------------------------------------------------------------------------
/** Drops the spaces and dashes people type between the digit groups. */
export function normalizeOntarioHealthNumber(value) {
    return value.replace(/[\s-]/g, "");
}
/**
 * Ten digits whose last is the mod-10 (Luhn) check digit of the first nine:
 * digits 1, 3, 5, 7 and 9 are doubled (less 9 when over 9), and the sum of
 * every digit, check digit included, is a multiple of ten. 0000000000 passes
 * that sum and is rejected explicitly.
 */
export function isValidOntarioHealthNumber(value) {
    const digits = normalizeOntarioHealthNumber(value);
    if (!/^\d{10}$/.test(digits) || digits === "0000000000")
        return false;
    let sum = 0;
    for (let index = 0; index < digits.length; index += 1) {
        let digit = Number(digits[index]);
        if (index % 2 === 0) {
            digit *= 2;
            if (digit > 9)
                digit -= 9;
        }
        sum += digit;
    }
    return sum % 10 === 0;
}
/** A patient-typed Ontario health number, stored as its ten digits. */
export const OntarioHealthNumberSchema = z
    .string()
    .transform(normalizeOntarioHealthNumber)
    .pipe(z
    .string()
    .min(1, { message: "Enter your Ontario health card number", abort: true })
    .regex(/^\d{10}$/, {
    message: "Enter the 10 digits of the health card number",
    abort: true,
})
    .refine(isValidOntarioHealthNumber, "Check the health card number; it does not match an Ontario health card"));
/**
 * The version code printed after the health number: one or two letters, stored
 * upper case. Unlike the pinned HealthCardVersionCodeInputSchema there is no
 * empty value: a patient who does not know it leaves it out.
 */
export const OntarioHealthCardVersionCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{1,2}$/, "The version code is one or two letters");
/**
 * Year 0000 passes z.iso.date() but is no date PostgreSQL can hold, so a stored
 * questionnaire could not be registered from it; the database refuses it too.
 */
function isNotYearZero(value) {
    return !value.startsWith("0000-");
}
/**
 * The full expiry date printed on a photo health card (RegisterPatientInput
 * and api.register_patient take only a full date).
 * Red-and-white cards have none.
 */
export const OntarioHealthCardExpiryDateSchema = z.iso
    .date("Enter the expiry date shown on your card")
    .refine(isNotYearZero, "Enter the expiry date shown on your card");
/** `A1A 1A1`, typed in any case with or without the space. */
export const CanadianPostalCodeSchema = z
    .string()
    .transform((value) => value.replace(/\s+/g, "").toUpperCase())
    .pipe(z
    .string()
    .regex(/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/, "Enter a postal code like M5V 2T6"))
    .transform((value) => `${value.slice(0, 3)} ${value.slice(3)}`);
// ---------------------------------------------------------------------------
// The questionnaire
// ---------------------------------------------------------------------------
/** A UTF-16 surrogate standing alone: half of an emoji cut off by a copy, say. */
const LONE_SURROGATE = /\p{Cs}/u;
/**
 * Whether PostgreSQL text and jsonb can hold the value. They cannot hold U+0000
 * or a lone surrogate, and a database call carrying either fails before any
 * check runs (PostgREST refuses the whole request body), so both are refused
 * here with a message instead.
 */
function isStorableText(value) {
    return !value.includes("\u0000") && !LONE_SURROGATE.test(value);
}
/** Free text the patient types. */
function patientText(label) {
    return z
        .string()
        .trim()
        .refine(isStorableText, `${label} contains a character that cannot be saved`);
}
function requiredText(max, label, required) {
    return patientText(label)
        .min(1, required)
        .max(max, `${label} must be ${max} characters or fewer`);
}
function optionalText(max, label) {
    return patientText(label)
        .min(1, `${label} cannot be blank`)
        .max(max, `${label} must be ${max} characters or fewer`)
        .optional();
}
function planMemberId(label, required) {
    return patientText(label)
        .min(1, { message: required, abort: true })
        .min(2, `${label} must be at least 2 characters`)
        .max(120, `${label} must be 120 characters or fewer`);
}
export const PatientQuestionnaireEmailSchema = z
    .string()
    .trim()
    .email("Enter a valid email address or leave it blank")
    .max(254, "Email must be 254 characters or fewer");
/** A Canadian home address; `region` is the key staff forms write and forms read first. */
export const PatientQuestionnaireAddressSchema = z
    .object({
    line1: optionalText(200, "Address line 1"),
    line2: optionalText(200, "Address line 2"),
    city: optionalText(120, "City"),
    region: CanadianProvinceSchema,
    postalCode: CanadianPostalCodeSchema.optional(),
    country: z.literal("CA"),
})
    .strict();
export const PatientQuestionnaireEmergencyContactSchema = z
    .object({
    name: requiredText(200, "Emergency contact name", "Emergency contact needs a name"),
    relationship: optionalText(80, "Relationship"),
    phone: requiredText(40, "Emergency contact phone", "Emergency contact needs a phone number"),
})
    .strict();
/** Ontario health card: the only plan with a version code and an expiry date. */
export const PatientQuestionnaireOhipCoverageSchema = z
    .object({
    kind: z.literal("insurance"),
    payerKind: z.literal("provincial_plan"),
    payerName: z.literal(OHIP_PAYER_NAME),
    memberId: OntarioHealthNumberSchema,
    relationship: z.literal("self"),
    versionCode: OntarioHealthCardVersionCodeSchema.optional(),
    expiryDate: OntarioHealthCardExpiryDateSchema.optional(),
})
    .strict();
/** Another province's or territory's plan; its number has no format check. */
export const PatientQuestionnaireOtherProvinceCoverageSchema = z
    .object({
    kind: z.literal("insurance"),
    payerKind: z.literal("provincial_plan"),
    payerName: z.enum(OTHER_PROVINCIAL_PLAN_NAMES),
    memberId: planMemberId("Health card number", "Enter your health card number"),
    relationship: z.literal("self"),
})
    .strict();
/** Interim Federal Health Program, identified by the client ID (UCI). */
export const PatientQuestionnaireIfhCoverageSchema = z
    .object({
    kind: z.literal("insurance"),
    payerKind: z.literal("federal_program"),
    payerName: z.literal(IFH_PAYER_NAME),
    memberId: planMemberId("IFH client ID", "Enter your IFH client ID (UCI)"),
    relationship: z.literal("self"),
})
    .strict();
/**
 * Four choices, each a strict subset of PatientCoverageUpdateInputSchema:
 * no coverage (`cash`), OHIP, another province's plan, or IFH. Staff reconcile
 * anything else at the visit.
 */
export const PatientQuestionnaireCoverageSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("cash") }).strict(),
    z.discriminatedUnion("payerName", [
        PatientQuestionnaireOhipCoverageSchema,
        PatientQuestionnaireOtherProvinceCoverageSchema,
        PatientQuestionnaireIfhCoverageSchema,
    ], { error: "Choose the province or territory that issued your health card" }),
], { error: "Choose your health coverage" });
/** The RegisterPatientInput identity keys, every one stricter or equal. */
export const PatientQuestionnaireIdentitySchema = RegisterPatientInputSchema.omit({
    coverage: true,
}).extend({
    legalFirstName: requiredText(100, "Legal first name", "First name is required"),
    legalMiddleName: optionalText(100, "Legal middle name"),
    legalLastName: requiredText(100, "Legal last name", "Last name is required"),
    preferredName: optionalText(120, "Preferred name"),
    dateOfBirth: z.iso
        .date({
        error: (issue) => issue.input === "" ? "Date of birth is required" : "Enter a real date of birth",
    })
        .refine(isNotYearZero, "Enter a real date of birth"),
    administrativeSex: z.enum(PatientAdministrativeSexSchema.enum, { error: "Sex is required" }),
    phone: requiredText(40, "Mobile number", "Mobile number is required"),
    email: PatientQuestionnaireEmailSchema.optional(),
    address: PatientQuestionnaireAddressSchema.optional(),
    emergencyContact: PatientQuestionnaireEmergencyContactSchema.optional(),
});
export const PatientQuestionnairePregnancySchema = z.enum(["yes", "no", "not-applicable", "prefer-not-to-say"], { error: "Answer the pregnancy question" });
/** The visit keys: everything a questionnaire has beyond RegisterPatientInput. */
export const PATIENT_QUESTIONNAIRE_VISIT_KEYS = [
    "reason",
    "symptoms",
    "allergies",
    "medications",
    "medicalHistory",
    "pregnant",
];
/** Coverage and the visit keys: what every questionnaire asks, with or without identity. */
const questionnaireCoverageAndVisitShape = {
    coverage: PatientQuestionnaireCoverageSchema,
    reason: requiredText(1_000, "Reason for visit", "Tell us why you are visiting"),
    symptoms: z
        .array(patientText("A symptom")
        .min(1, "Symptoms cannot be blank")
        .max(120, "Each symptom must be 120 characters or fewer"))
        .max(30, "Choose at most 30 symptoms"),
    allergies: patientText("Allergies").max(2_000, "Allergies must be 2000 characters or fewer"),
    medications: patientText("Medications").max(2_000, "Medications must be 2000 characters or fewer"),
    medicalHistory: patientText("Medical history").max(4_000, "Medical history must be 4000 characters or fewer"),
    pregnant: PatientQuestionnairePregnancySchema,
};
function refineUniqueSymptoms(value, context) {
    if (new Set(value.symptoms).size !== value.symptoms.length) {
        context.addIssue({
            code: "custom",
            message: "Each symptom can be listed only once",
            path: ["symptoms"],
        });
    }
}
export const PatientQuestionnaireSchema = PatientQuestionnaireIdentitySchema.extend(questionnaireCoverageAndVisitShape)
    .strict()
    // Refinements stay on this final schema: Zod refuses to extend a refined object.
    .superRefine(refineUniqueSymptoms);
// ---------------------------------------------------------------------------
// Questionnaires that leave About you out (PA-5)
// ---------------------------------------------------------------------------
/** The About you keys. A questionnaire that did not ask for identity has none of them. */
export const PATIENT_QUESTIONNAIRE_IDENTITY_KEYS = PatientQuestionnaireIdentitySchema.keyof().options;
/**
 * A questionnaire without About you: coverage and the visit keys only, with the full
 * questionnaire's rules. A walk-in whose chart was not created by this visit (a returning
 * patient, PA-5) is asked only these; the chart already holds who they are. Any identity key is
 * refused like any unknown key.
 */
export const ReturningPatientQuestionnaireSchema = z
    .object(questionnaireCoverageAndVisitShape)
    .strict()
    .superRefine(refineUniqueSymptoms);
/**
 * What an intake link submits: the full questionnaire, or the one without About you when the
 * link did not ask for identity (the session's questionnaireSections). The server checks the
 * shape against the link, never the client's choice.
 */
export const PatientIntakeAnswersSchema = z.union([
    PatientQuestionnaireSchema,
    ReturningPatientQuestionnaireSchema,
]);
/** Whether the answers include About you (the full PA-2 questionnaire). */
export function isFullPatientQuestionnaire(answers) {
    return Object.hasOwn(answers, "legalFirstName");
}
// ---------------------------------------------------------------------------
// The draft (form state)
// ---------------------------------------------------------------------------
function draftText(max) {
    return z.string().trim().max(max);
}
export const PatientQuestionnaireCoverageChoiceSchema = z.enum([
    "ohip",
    "other_province",
    "ifh",
    "none",
]);
/**
 * The flat form state. Strings may be blank and carry only a length cap; the
 * rules apply when patientQuestionnaireFromDraft compacts it. Caps are at
 * least the strict ones and at least the booking ones the demo lane prefills
 * from (display name split into first and last name up to 240, phone up to
 * 60), so a prefill never fails to parse; an over-long prefilled value is
 * reported to the patient instead.
 */
export const PatientQuestionnaireDraftSchema = z
    .object({
    legalFirstName: draftText(240),
    legalMiddleName: draftText(240),
    legalLastName: draftText(240),
    preferredName: draftText(240),
    dateOfBirth: z.union([z.iso.date(), z.literal("")]),
    administrativeSex: z.union([PatientAdministrativeSexSchema, z.literal("")]),
    phone: draftText(60),
    email: draftText(254),
    addressLine1: draftText(200),
    addressLine2: draftText(200),
    city: draftText(120),
    province: CanadianProvinceSchema,
    postalCode: draftText(10),
    emergencyContactName: draftText(200),
    emergencyContactRelationship: draftText(80),
    emergencyContactPhone: draftText(40),
    coverageChoice: z.union([PatientQuestionnaireCoverageChoiceSchema, z.literal("")]),
    healthNumber: draftText(20),
    versionCode: draftText(2),
    expiryDate: z.union([z.iso.date(), z.literal("")]),
    otherPlanProvince: z.union([OtherCanadianProvinceSchema, z.literal("")]),
    otherPlanNumber: draftText(120),
    ifhNumber: draftText(120),
    reason: draftText(1_000),
    symptoms: z.array(z.string().trim().min(1).max(120)).max(30),
    allergies: draftText(2_000),
    medications: draftText(2_000),
    medicalHistory: draftText(4_000),
    pregnant: PatientQuestionnairePregnancySchema,
})
    .strict();
/** A fresh blank draft: Ontario address, no coverage chosen. */
export function emptyPatientQuestionnaireDraft() {
    return {
        legalFirstName: "",
        legalMiddleName: "",
        legalLastName: "",
        preferredName: "",
        dateOfBirth: "",
        administrativeSex: "",
        phone: "",
        email: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        province: "ON",
        postalCode: "",
        emergencyContactName: "",
        emergencyContactRelationship: "",
        emergencyContactPhone: "",
        coverageChoice: "",
        healthNumber: "",
        versionCode: "",
        expiryDate: "",
        otherPlanProvince: "",
        otherPlanNumber: "",
        ifhNumber: "",
        reason: "",
        symptoms: [],
        allergies: "",
        medications: "",
        medicalHistory: "",
        pregnant: "not-applicable",
    };
}
/** The three parts of the form, in the order the patient app shows them. */
export const PATIENT_QUESTIONNAIRE_SECTIONS = ["identity", "coverage", "health"];
const [IDENTITY_SECTION, COVERAGE_SECTION, HEALTH_SECTION] = PATIENT_QUESTIONNAIRE_SECTIONS;
/** Every section: a booking's questionnaire, or a walk-in whose chart this visit created. */
export const FULL_PATIENT_INTAKE_SECTIONS = PATIENT_QUESTIONNAIRE_SECTIONS;
/** Coverage and health only: a walk-in on a chart that existed before this visit (PA-5). */
export const RETURNING_PATIENT_INTAKE_SECTIONS = [COVERAGE_SECTION, HEALTH_SECTION];
/**
 * The sections an intake link asks for, in form order: all three, or coverage and health
 * without About you. Coverage and health are always asked; only identity is ever left out.
 */
export const PatientIntakeSectionsSchema = z.union([
    z
        .tuple([z.literal(IDENTITY_SECTION), z.literal(COVERAGE_SECTION), z.literal(HEALTH_SECTION)])
        .readonly(),
    z.tuple([z.literal(COVERAGE_SECTION), z.literal(HEALTH_SECTION)]).readonly(),
]);
/** Whether the link asks About you. */
export function patientIntakeAsksIdentity(sections) {
    return sections[0] === IDENTITY_SECTION;
}
/**
 * The stored submission's schema version (scheduling.intake_submissions.schema_version): 1 is
 * the full PA-2 questionnaire, 2 the one without About you (ReturningPatientQuestionnaireSchema).
 */
export const PATIENT_INTAKE_SCHEMA_VERSION = { full: 1, withoutIdentity: 2 };
export function patientIntakeSchemaVersion(sections) {
    return patientIntakeAsksIdentity(sections)
        ? PATIENT_INTAKE_SCHEMA_VERSION.full
        : PATIENT_INTAKE_SCHEMA_VERSION.withoutIdentity;
}
const SECTION_BY_DRAFT_FIELD = {
    legalFirstName: "identity",
    legalMiddleName: "identity",
    legalLastName: "identity",
    preferredName: "identity",
    dateOfBirth: "identity",
    administrativeSex: "identity",
    phone: "identity",
    email: "identity",
    addressLine1: "identity",
    addressLine2: "identity",
    city: "identity",
    province: "identity",
    postalCode: "identity",
    emergencyContactName: "identity",
    emergencyContactRelationship: "identity",
    emergencyContactPhone: "identity",
    coverageChoice: "coverage",
    healthNumber: "coverage",
    versionCode: "coverage",
    expiryDate: "coverage",
    otherPlanProvince: "coverage",
    otherPlanNumber: "coverage",
    ifhNumber: "coverage",
    reason: "health",
    symptoms: "health",
    allergies: "health",
    medications: "health",
    medicalHistory: "health",
    pregnant: "health",
};
export function patientQuestionnaireSectionOf(field) {
    return SECTION_BY_DRAFT_FIELD[field];
}
const ADDRESS_DRAFT_FIELDS = {
    line1: "addressLine1",
    line2: "addressLine2",
    city: "city",
    region: "province",
    postalCode: "postalCode",
    country: "province",
};
const EMERGENCY_CONTACT_DRAFT_FIELDS = {
    name: "emergencyContactName",
    relationship: "emergencyContactRelationship",
    phone: "emergencyContactPhone",
};
const MEMBER_ID_DRAFT_FIELDS = {
    ohip: "healthNumber",
    other_province: "otherPlanNumber",
    ifh: "ifhNumber",
};
function isDraftField(key) {
    return typeof key === "string" && Object.hasOwn(SECTION_BY_DRAFT_FIELD, key);
}
function draftFieldFor(path, draft) {
    const [head, child] = path;
    const childKey = typeof child === "string" ? child : "";
    if (head === "address")
        return ADDRESS_DRAFT_FIELDS[childKey] ?? "addressLine1";
    if (head === "emergencyContact") {
        return EMERGENCY_CONTACT_DRAFT_FIELDS[childKey] ?? "emergencyContactName";
    }
    if (head === "coverage") {
        if (childKey === "versionCode" || childKey === "expiryDate")
            return childKey;
        if (childKey === "memberId" && draft.coverageChoice !== "") {
            return MEMBER_ID_DRAFT_FIELDS[draft.coverageChoice] ?? "coverageChoice";
        }
        if (childKey === "payerName" && draft.coverageChoice === "other_province") {
            return "otherPlanProvince";
        }
        return "coverageChoice";
    }
    // The candidate is built from draft keys only, so every other path starts with one.
    return isDraftField(head) ? head : "legalFirstName";
}
function present(key, value) {
    return value.trim() === "" ? {} : { [key]: value };
}
function coverageCandidate(draft) {
    switch (draft.coverageChoice) {
        case "none":
            return { coverage: { kind: "cash" } };
        case "ohip":
            return {
                coverage: {
                    kind: "insurance",
                    payerKind: "provincial_plan",
                    payerName: OHIP_PAYER_NAME,
                    memberId: draft.healthNumber,
                    relationship: "self",
                    ...present("versionCode", draft.versionCode),
                    ...present("expiryDate", draft.expiryDate),
                },
            };
        case "other_province":
            return {
                coverage: {
                    kind: "insurance",
                    payerKind: "provincial_plan",
                    ...(draft.otherPlanProvince === ""
                        ? {}
                        : { payerName: provincialPlanName(draft.otherPlanProvince) }),
                    memberId: draft.otherPlanNumber,
                    relationship: "self",
                },
            };
        case "ifh":
            return {
                coverage: {
                    kind: "insurance",
                    payerKind: "federal_program",
                    payerName: IFH_PAYER_NAME,
                    memberId: draft.ifhNumber,
                    relationship: "self",
                },
            };
        case "":
            return {};
    }
}
/** The About you part of the compacted candidate, from draft keys only. */
function identityCandidate(draft) {
    const hasAddress = [draft.addressLine1, draft.addressLine2, draft.city, draft.postalCode].some((value) => value.trim() !== "");
    const hasEmergencyContact = [
        draft.emergencyContactName,
        draft.emergencyContactRelationship,
        draft.emergencyContactPhone,
    ].some((value) => value.trim() !== "");
    return {
        legalFirstName: draft.legalFirstName,
        ...present("legalMiddleName", draft.legalMiddleName),
        legalLastName: draft.legalLastName,
        ...present("preferredName", draft.preferredName),
        dateOfBirth: draft.dateOfBirth,
        administrativeSex: draft.administrativeSex,
        phone: draft.phone,
        ...present("email", draft.email),
        ...(hasAddress
            ? {
                address: {
                    ...present("line1", draft.addressLine1),
                    ...present("line2", draft.addressLine2),
                    ...present("city", draft.city),
                    region: draft.province,
                    ...present("postalCode", draft.postalCode),
                    country: "CA",
                },
            }
            : {}),
        ...(hasEmergencyContact
            ? {
                emergencyContact: {
                    name: draft.emergencyContactName,
                    ...present("relationship", draft.emergencyContactRelationship),
                    phone: draft.emergencyContactPhone,
                },
            }
            : {}),
    };
}
/** The coverage and visit part of the compacted candidate. */
function coverageAndVisitCandidate(draft) {
    return {
        ...coverageCandidate(draft),
        reason: draft.reason,
        symptoms: draft.symptoms,
        allergies: draft.allergies,
        medications: draft.medications,
        medicalHistory: draft.medicalHistory,
        pregnant: draft.pregnant,
    };
}
/** Parses a compacted candidate and maps every problem back to the draft field it came from. */
function compactDraft(schema, candidate, draft, verifiedDateOfBirth) {
    const issues = [];
    const addIssue = (field, message) => {
        if (!issues.some((issue) => issue.field === field && issue.message === message)) {
            issues.push({ field, section: SECTION_BY_DRAFT_FIELD[field], message });
        }
    };
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
        for (const issue of parsed.error.issues) {
            addIssue(draftFieldFor(issue.path, draft), issue.message);
        }
    }
    // Without a province the plan cannot be told apart, so its number is checked on its own.
    if (draft.coverageChoice === "other_province" && draft.otherPlanProvince === "") {
        const memberId = PatientQuestionnaireOtherProvinceCoverageSchema.shape.memberId.safeParse(draft.otherPlanNumber);
        for (const issue of memberId.error?.issues ?? [])
            addIssue("otherPlanNumber", issue.message);
    }
    if (verifiedDateOfBirth !== undefined &&
        draft.dateOfBirth !== "" &&
        draft.dateOfBirth !== verifiedDateOfBirth) {
        addIssue("dateOfBirth", "Date of birth must match the one you entered to open this link");
    }
    if (parsed.success && issues.length === 0)
        return { success: true, data: parsed.data };
    return { success: false, issues };
}
/**
 * Compacts the form state into the questionnaire a submit sends: values
 * trimmed, blank optional fields left out, the address left out when no
 * street, city or postal code is given (otherwise it takes the province and
 * country CA), the emergency contact left out when all three fields are blank,
 * and only the chosen coverage kept. Fails with one patient-facing message per
 * problem, each naming the draft field and form section it belongs to.
 */
export function patientQuestionnaireFromDraft(draft, options = {}) {
    return compactDraft(PatientQuestionnaireSchema, { ...identityCandidate(draft), ...coverageAndVisitCandidate(draft) }, draft, options.verifiedDateOfBirth);
}
/**
 * The answers an intake link submits, compacted as patientQuestionnaireFromDraft
 * does. With every section asked it is exactly that questionnaire. Without About
 * you the identity fields are left out whatever they hold (a draft may carry a
 * prefilled date of birth), the answers parse as ReturningPatientQuestionnaire,
 * and only coverage and health problems are reported.
 */
export function patientIntakeAnswersFromDraft(draft, options = {}) {
    if (options.sections === undefined || patientIntakeAsksIdentity(options.sections)) {
        return patientQuestionnaireFromDraft(draft, options);
    }
    return compactDraft(ReturningPatientQuestionnaireSchema, coverageAndVisitCandidate(draft), draft, undefined);
}
/**
 * The patient-facing problems with a draft; empty when it would submit. Only
 * the asked sections are checked (all three by default).
 */
export function patientQuestionnaireDraftIssues(draft, options = {}) {
    return patientIntakeAnswersFromDraft(draft, options).issues ?? [];
}
// ---------------------------------------------------------------------------
// Registration and staff follow-up
// ---------------------------------------------------------------------------
const REGISTER_PATIENT_INPUT_KEYS = RegisterPatientInputSchema.keyof().options;
/**
 * The RegisterPatientInput a questionnaire carries: its identity and coverage
 * keys as they are, nothing renamed. PA-6 registers the patient with it.
 */
export function toRegisterPatientInput(questionnaire) {
    const picked = {};
    for (const key of REGISTER_PATIENT_INPUT_KEYS) {
        if (questionnaire[key] !== undefined)
            picked[key] = questionnaire[key];
    }
    return RegisterPatientInputSchema.parse(picked);
}
export const HealthCardFollowUpSchema = z.enum([
    "version_code_not_given",
    "expiry_not_given",
    "card_expired",
]);
/**
 * What staff confirm about a patient-entered Ontario health card. The patient
 * may leave the version code and expiry blank and is never blocked; staff see
 * these flags instead (PA-2 acceptance; rendered in PA-6). Every other coverage
 * needs none. `today` is the clinic-local date, `YYYY-MM-DD`; a card whose
 * printed expiry is before it has expired.
 */
export function healthCardFollowUps(coverage, today) {
    if (coverage.kind !== "insurance" || coverage.payerName !== OHIP_PAYER_NAME)
        return [];
    const followUps = [];
    if (coverage.versionCode === undefined)
        followUps.push("version_code_not_given");
    if (coverage.expiryDate === undefined)
        followUps.push("expiry_not_given");
    else if (coverage.expiryDate < today)
        followUps.push("card_expired");
    return followUps;
}
//# sourceMappingURL=patient-questionnaire.js.map